/**
 * Result screen panel showing keyword and accuracy breakdowns after a SIM level completes.
 *
 * @module components/sim/takeSim/ResultScreenAccuracies
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import {
    Grid,
    Typography,
    Divider,
    Paper,
    Box, Tooltip, Button, Avatar,
} from '@material-ui/core';
import PropTypes from "prop-types";
import {connect} from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import {useStyles} from './TakeSimResult.js';
import ProfilePic from '../../../assets/images/profile_pic.png';
import moment from "moment";
import {useLocation} from "react-router-dom";
import loadingIcon from "../../../assets/images/icons/loading-1.gif";
import {
    userFinalScoreResponse,
} from "../../../redux/sims/actions";
import {formatElapsedTime} from "../../../utils/time";

/**
 * ResultScreenAccuracies is a React functional component responsible for rendering the results
 * of a user's simulation session. This component includes the user's details, simulation details,
 * keyword accuracy, and other related metrics.
 *
 * @param {Object} props - The props object containing the following properties:
 * @param {Object} props.userResult - An object containing the user's simulation results.
 * @param {Function} props.setUserResult - A state setter function to update the user's result.
 * @param {Object} props.user - An object representing the currently logged-in user.
 * @param {number} props.elapsedTime - Total elapsed time during the simulation session.
 * @param {boolean} props.isPost - Boolean indicating whether the session was a post-assessment.
 * @param {Function} props.userFinalScoreResponse - Function to update the user's final score.
 * @param {Object} props.caseValuesArray - An object containing configuration or case-specific values.
 * @param {Object} props.userResponse - An object representing the user's response.
 * @param {string} props.simTitle - Title of the simulation session.
 * @param {string} props.simProductType - Type of product associated with the simulation.
 * @param {Object} props.config - Configuration object with additional settings or flags.
 * @param {Function} props.sendAudioToSnowfly - Function to send audio data to Snowfly.
 * @param {boolean} props.sendingToSnowfly - Boolean indicating if audio is being sent to Snowfly.
 * @param {string} props.snowflyRecordId - Unique identifier for Snowfly record.
 *
 * @returns {JSX.Element} The rendered JSX layout for the result screen view.
 */
const ResultScreenAccuracies = ({
    userResult,
    setUserResult,
    user,
    elapsedTime,
    isPost,
    userFinalScoreResponse,
    caseValuesArray,
    userResponse,
    simTitle,
    simProductType,
    config,
    sendAudioToSnowfly,
    sendingToSnowfly,
    snowflyRecordId,
}) => {
    const classes = useStyles();
    const loggedUser = JSON.parse(localStorage.getItem('user'));
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const assignment_id = urlParams.get("assignment_id");
    const order = Number(urlParams.get("order"))+1; // This is the level order, zero based (order=4 means level 5)

    const [snowflyId, setSnowflyId] = useState('');

    useEffect(
        () => {
            setSnowflyId(snowflyRecordId);
        },
        [snowflyRecordId]
    );

    useEffect(() => {
        if (userResult && userResult.scripts && userResult.scripts.length) {
            setUserResult(userResult);
            if (!snowflyId && (userResult?.level?.snowfly_id || userResult?.snowfly_id)) {
                setSnowflyId(userResult?.level?.snowfly_id || userResult?.snowfly_id);
            }
            //debug("userResult = %o", userResult);
        }
    }, [userResult, setUserResult, snowflyId]);

    /**
     * Executes an API call at specified intervals with the provided payload.
     *
     * This method constructs a payload object by combining `caseValuesArray` data,
     * a flag to indicate a POST request, an optional `assignment_id`, and the
     * elapsed time. If the `isPost` flag is not explicitly set to `false`, it
     * triggers the `userFinalScoreResponse` function with the appropriate user ID
     * and payload.
     *
     * Variables Referenced:
     * - `caseValuesArray`: A data structure containing case values to be included
     *   in the payload.
     * - `assignment_id`: Optional ID related to a specific assignment.
     * - `elapsedTime`: Time elapsed since a specific action or event.
     * - `isPost`: Flag to determine if the payload should trigger a POST request.
     * - `userResponse._id`: Unique user identifier used to associate the API call.
     *
     * The function is designed to ensure conditional execution of the POST request
     * based on the `isPost` flag.
     */
    const callApiInInterval = () => {
        const payload = {
            ...caseValuesArray,
            isPost: true,
            ...(assignment_id ? { assignment_id } : {}),
            elapsedTime: elapsedTime,
        };
        if (isPost !== false) {
            userFinalScoreResponse(userResponse._id, payload);
        }
    };

    /**
     * Renders a circular progress indicator with a label. The label can dynamically display text,
     *      progress values, or other content based on the provided props and application state.
     *
     * @param {Object} props - The properties passed to the component. Typically, includes
     *      configuration for the CircularProgress component, such as value, color, size, and
     *      additional styling.
     * @return {JSX.Element} The rendered component, a circular progress element with a dynamic
     *      label in the center.
     */
    function CircularProgressWithLabel(props) {
        const allUserScriptsLoaded = userResult.agent_scripts &&
          userResult.agent_scripts.every((ele) => ele.status === "Completed");

        if (userResult.agent_scripts && !allUserScriptsLoaded) {
            setTimeout(() => {
                callApiInInterval();
            }, 5000);
        }

        return (
            <div>
                <Box position="relative" display="inline-flex" className={classes.prog}>
                    <CircularProgress
                        variant="determinate"
                        size="6rem"
                        thickness={2}
                        {...props}
                        className={classes.circularProg}
                    />
                    <Box
                        top={0}
                        left={0}
                        bottom={0}
                        right={0}
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="caption"
                            component="div"
                            className={classes.progTypo}
                        >
                            {(userResult &&
                                Object.keys(userResult).length !== 0 &&
                                userResult.agent_scripts.length &&
                                userResult.agent_scripts[0].audio_url !== ""
                            ) ? (
                                allUserScriptsLoaded ? (
                                    isNaN(userResult.keyword_accuracy) ?
                                        (
                                            `N/A`
                                        ) :
                                        (
                                            `${Math.min(100, Math.max(0, isNaN(userResult.keyword_accuracy)))}%`
                                        )
                                ) : (
                                    <img height="40px" src={loadingIcon} alt="loading..." />
                                )
                            ) : (
                                "0%"
                            )}
                        </Typography>
                    </Box>
                </Box>
            </div>
        );
    }

    /**
     * Represents a button component that allows users to send audio data to Snowfly if certain conditions are met.
     *
     * This function evaluates whether the Snowfly integration is enabled and if the user has permissions or data required
     * to send the audio. It dynamically renders a button or related UI elements based on the current state.
     *
     * Conditions:
     * - The Snowfly audio merge must be enabled (`config.snowFlyAudioMerge`).
     * - The user result object (`userResult`) must exist.
     * - At least one of the following must be true:
     *   - `userResult.level.send_to_snowfly` exists and is truthy.
     *   - `userResult.send_to_snowfly` exists and is truthy.
     * - A valid Snowfly ID (`userResult.snowfly_id` or `snowflyId`) is not required to render the button but will modify its appearance and behavior.
     *
     * Render Behavior:
     * - If the Snowfly ID does not exist:
     *   - Displays a tooltip and a button labeled "Send to Snowfly."
     *   - Button is disabled if an ongoing send operation (`sendingToSnowfly`) is in progress.
     *   - Clicking the button triggers the `sendAudioToSnowfly` function to initiate the audio send process.
     * - If the Snowfly ID exists:
     *   - Displays the Snowfly ID in uppercase.
     * - Renders a placeholder (e.g., "--------") if none of the conditions for displaying the button are satisfied.
     *
     * Visual Enhancements:
     * - The button includes a spinner (`CircularProgress`) and a "Sending..." label during the send operation.
     * - Styling options include font size, weight, padding, and display options, with dynamic adjustments based on the Snowfly ID's presence.
     *
     */
    const sendToSnowflyButton = () => {
        if(
            config.snowFlyAudioMerge &&
            userResult &&
            ((userResult.level && userResult.level.send_to_snowfly) || userResult.send_to_snowfly)
        ) {
            if (!(userResult.snowfly_id || snowflyId)) {
                return (
                    <Tooltip
                        title='Send the current SIM level audio to Snowfly'
                        placement="bottom"
                    >
                        <span>
                            <Button
                                variant="contained"
                                onClick={sendAudioToSnowfly}
                                disabled={sendingToSnowfly}
                                style={{
                                    display: (snowflyId) ? 'none' : 'default',
                                    fontSize: '0.85em',
                                    fontWeight: '400',
                                    padding: '0.5em 0.75em',
                                    lineHeight: '1em'
                                }}
                            >
                                {sendingToSnowfly && (
                                    <>
                                        <CircularProgress color="primary" size={"2em"}/>
                                        <span style={{marginLeft: '1em', color: 'white'}}>Sending...</span>
                                    </>
                                )}
                                Send to Snowfly
                            </Button>
                        </span>
                    </Tooltip>
                );
            } else {
                return (
                    <span>{snowflyId.toUpperCase()}</span>
                );
            }
        }

        return (
            <span>--------</span>
        )
    };

    console.log('userResult = %o', userResult);

    return (
        <Grid container className={classes.girdContainer} item md={3}>
            <div className={classes.userDetails}>
                <div className={classes.userImage}>
                    <Avatar
                        height={47}
                        width={46}
                        alt="profile"
                        className={classes.avatar}
                        src={userResult.profile_pic_url ? userResult.profile_pic_url : ProfilePic}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = ProfilePic;
                        }}
                    />
                </div>
                <div style={{display: 'inline-block'}}>
                    <Typography className={classes.name}>
                        {userResult?.user?.first_name
                            ? `${userResult?.user?.first_name} ${userResult?.user?.last_name}`
                            : `${user.first_name} ${user.last_name}`}
                    </Typography>
                    <span className={classes.simTitle}>{simTitle ? simTitle : userResult.simTitle}</span>
                    <br/>
                    <Typography variant="caption" className={classes.details}>
                        {userResult ? (
                            moment(userResult.completed_on).format("MM-DD-YYYY h:mm A")
                        ) : (
                            <span>Loading...</span>
                        )}
                        <br />
                        Mode:{" "}
                        {userResult?.mode?.charAt(0).toUpperCase() + userResult?.mode?.slice(1)}
                    </Typography>
                </div>
                <Divider variant="middle" className={classes.divider} />

                <div className={classes.select}>
                    <Typography className={classes.details}>
                        Level {parseInt(order)} : {userResult?.scripts?.length &&
                        userResult?.scripts[0]?.sim_level_id?.name}
                    </Typography>
                </div>
                <div className={classes.contentDiv}>
                    <div className={classes.subDiv}>
                        <Typography className={classes.details}>Total Time</Typography>
                        <Typography className={classes.details}>:</Typography>
                        <Typography className={classes.details}>
                            {formatElapsedTime(userResult?.totalSeconds ?? 0)}
                        </Typography>
                    </div>
                    <Divider variant="middle" className={classes.dividerContent}/>
                    <div className={classes.subDiv}>
                        <Typography className={classes.details}>Wrap Up Time</Typography>
                        <Typography className={classes.details}>:</Typography>
                        <Typography className={classes.details}>
                            {userResult?.wrapSeconds ? formatElapsedTime(userResult?.wrapSeconds ?? 0) : 'N/A'}
                        </Typography>
                    </div>
                    <Divider variant="middle" className={classes.dividerContent}/>
                    <div className={classes.subDiv}>
                        <Typography className={classes.details}>Snowfly ID</Typography>
                        <Typography className={classes.details}>:</Typography>
                        <Typography className={classes.details}>{sendToSnowflyButton()}</Typography>
                    </div>
                </div>
            </div>

            <Paper className={classes.accuracyPaper}>
                {(loggedUser.role).toLowerCase() === 'agent' ?
                    userResult?.scripts?.length && userResult?.scripts[0]?.sim_level_id?.keyword_score ?
                        <div className={classes.accuracySection} style={{lineHeight: '0.5rem'}}>
                            <Typography className={classes.keyTypo}>
                                Keyword Accuracy
                            </Typography>
                            {
                                userResult?.keyword_accuracy ?
                                    (order === '0' || order === '1') ?
                                        <div className={classes.keywordAccuracy}>
                                            <span className={classes.blackColor}>N/A</span>
                                        </div> :
                                        <CircularProgressWithLabel
                        value={Math.min(100, Math.max(0, +userResult?.keyword_accuracy ?? 0))}
                                        /> :
                                        <div className={classes.keywordAccuracy}>
                                            <span className={classes.blackColor}>
                                                {(order === '0' || order === '1') ? 'N/A' : '0%'}
                                            </span>
                                        </div>
                            }
                        </div> :''
                  :
                  userResult?.scripts?.length && userResult?.scripts[0]?.sim_level_id?.keyword_score ?
                    <div className={classes.accuracySection} style={{lineHeight: '0.5rem'}}>
                      <Typography className={classes.keyTypo}>
                        Keyword Accuracy
                      </Typography>
                      {
                            userResult?.keyword_accuracy ?
                                (order === '0' || order === '1') ?
                                    <div className={classes.keywordAccuracy}>
                                        <span className={classes.blackColor}>N/A</span>
                                    </div> :
                                <CircularProgressWithLabel
                                    value={Math.min(100, Math.max(0, +userResult?.keyword_accuracy ?? 0))}
                                /> :
                                <div className={classes.keywordAccuracy}>
                                    <span className={classes.blackColor}>{(order === '0' || order === '1') ? 'N/A' : '0%'}</span>
                                </div>
                      }
                    </div>
                  : ''
                }
                {
                    !simProductType.includes('Voice Only') &&
                    <div>
                        <Typography className={classes.accuracySection}>
                            Typing Accuracy
                            <br/>
                            {userResult.auto_populate ?
                                <span className={classes.numberColor}>N/A</span> :
                                <span className={classes.numbers}>
                                    <span className={classes.numberColor}>
                                        <span className={classes.blackColor}>
                                            {userResult.correct_entries}
                                        </span>
                                        {" "} of {userResult.total_entries}
                                    </span>
                                    <br/>
                                    (Correct Entries)
                                </span>
                            }
                        </Typography>
                    </div>
                }
                {
                    !simProductType.includes('Voice Only') &&
                    <div>
                        <Typography className={classes.accuracySection}>
                            Data Selection Accuracy
                            <br />
                            {userResult.auto_populate ?
                                <span className={classes.numberColor}>N/A</span> :
                                <span className={classes.numbers}>
                                    <span className={classes.numberColor}>
                                        <span className={classes.blackColor}>
                                            {userResult.correct_selections}
                                        </span>
                                        {" "} of {userResult.total_selections}
                                    </span>
                                    <br />
                                    (Selected Accurately)
                                </span>
                            }
                        </Typography>
                    </div>
                }
                {
                    !simProductType.includes('Voice Only') &&
                    <div>
                        <Typography className={classes.accuracySection}>
                            Overall Data Accuracy
                            <br/>
                            {userResult.auto_populate ?
                                <span className={classes.numberColor}>N/A</span> :
                                <span className={classes.numbers}>
                                    <span className={classes.numberColor}>
                                        <span className={classes.blackColor}>
                                            {userResult.overall_percentage} %
                                        </span>
                                    </span>
                                </span>
                            }
                        </Typography>
                    </div>
                }
            </Paper>
        </Grid>
    );
};

ResultScreenAccuracies.propTypes = {
    elapsedTime: PropTypes.number, //string,
    totalSeconds: PropTypes.number,
    userFinalScoreResponse: PropTypes.func,
    isPost: PropTypes.bool,
    caseValuesArray: PropTypes.object,
    userResponse: PropTypes.object,
    simProductType: PropTypes.string,
    userResult: PropTypes.object,
    setUserResult: PropTypes.func,
    user: PropTypes.object,
    config: PropTypes.object,
    sendAudioToSnowfly: PropTypes.func,
    sendingToSnowfly: PropTypes.bool,
    snowflyRecordId: PropTypes.string,
    simTitle: PropTypes.string,
};

const mapStateToProps = (state) => ({
    user: state.login.user,
    elapsedTime: state.sim.elapsedTime,
    totalSeconds: state.sim.totalSeconds,
    caseValuesArray: state.sim.caseValuesArray,
    userResponse: state.sim.userResponse,
    userResult: state.sim.userResult,
    snowflyRecordId: state.sim.userResult.snowfly_id,
    simTitle: state.sim.simTitle,
    simProductType: state.sim.currentSimProductType,
});

const mapDispatchToProps = {
    userFinalScoreResponse: userFinalScoreResponse,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResultScreenAccuracies);

